import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { BarChart } from '@mui/x-charts/BarChart';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CalculateIcon from '@mui/icons-material/Calculate';
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

import VariablesForm from "./VariablesForm";
import { fetchViewValueModel } from "../../actions/valueActions";
import { convertAddThsndDivider } from "../helpers/stringFormat";
import { removeCommasFromValues } from "./valueFunctions";
// import ExportChartToPptButton from "../helpers/ExportChartToPptButton";

const greenShades = [
    '#4CAF50', // light green
    '#66BB6A', // medium green
    '#81C784', // lighter green
    '#A5D6A7', // very light green
    '#388E3C', // dark green
    '#2E7D32', // darker green
    '#1B5E20', // darkest green
    '#00C853', // vivid green
    '#00E676', // bright green
    '#69F0AE', // neon green
];

const redShades = [
    '#F44336', // light red
    '#E57373', // medium red
    '#EF5350', // lighter red
    '#FFCDD2', // very light red
    '#D32F2F', // dark red
    '#C62828', // darker red
    '#B71C1C', // darkest red
    '#FF5252', // vivid red
    '#FF1744', // bright red
    '#FF8A80', // neon red
];

const ViewValueModel = ({ id }) => {
    const dispatch = useDispatch()

    // create this _once_ and never recreate it
    const emptyList = useMemo(() => [], [])

    const viewValueModel = useSelector(state => state.value?.viewValueModel[id] ?? emptyList);
    const variables = useSelector(state => state.value.valueModel[id]?.variables)
    const product = useSelector(state => state.value.valueModel[id]?.product)

    // State for setting the height of arrow
    const chartRef = useRef(null);
    const [arrowHeight, setArrowHeight] = useState(0);
    const [distanceFromBottom, setDistanceFromBottom] = useState(0);

    const referenceValue = product?.price_alternative;
    const statusQuo = product?.status_quo

    // Fetch calculated value model from backend
    useEffect(() => {
        dispatch(fetchViewValueModel(id, {}))
        // eslint-disable-next-line react-hooks/exhaustive-deps  
    }, []);

    // Calculate the total height of the arrow to show possible price ranges once the chart renders
    useEffect(() => {
        if (!chartRef.current) return;

        const handleCalculation = () => {
            const chartElement = chartRef.current;
            const referenceBars = chartElement.querySelectorAll('.MuiBarElement-series-reference');
            const totalValueBars = chartElement.querySelectorAll('.MuiBarElement-series-totalValue');

            if (!totalValueBars.length) return;

            // Sum up heights and find tops/bottoms
            let totalValueHeight = 0;
            let totalValueBarBottom = 0;
            totalValueBars.forEach((bar, idx) => {
                const rect = bar.getBoundingClientRect();
                totalValueHeight += rect.height;
                if (idx === 0) totalValueBarBottom = rect.bottom;
            });

            let arrowHeight;
            let distanceFromBottom;

            const chartBottom = chartElement.getBoundingClientRect().bottom;
            const marginOffset = 32; // your existing offset
            
            if (statusQuo) {
                // No reference bar — arrow spans full totalValueHeight
                arrowHeight = totalValueHeight;
                distanceFromBottom = marginOffset + 50 //chartBottom - totalValueBarBottom - 346;
            } else {
                // Reference bar present
                if (!referenceBars.length) return;

                let referenceHeight = 0;
                let referenceBarTop = 0;
                referenceBars.forEach((bar, idx) => {
                    const rect = bar.getBoundingClientRect();
                    referenceHeight += rect.height;
                    if (idx === 0) referenceBarTop = rect.top;
                });

                arrowHeight = totalValueHeight - referenceHeight;
                distanceFromBottom = marginOffset + chartBottom - referenceBarTop;
            }

            setArrowHeight(arrowHeight);
            setDistanceFromBottom(distanceFromBottom);
        };

        // Give chart time to render
        requestAnimationFrame(() => {
            setTimeout(handleCalculation, 500);
        });
    }, [viewValueModel, statusQuo]);

    // Display the reference price
    const referenceValueSerie = {
        data: [referenceValue, null, null, null],
        label: 'Price of Alternative',
        valueFormatter: (value) => {
            if (value) {
                const formattedValue = convertAddThsndDivider(Math.abs(value)); // Format the absolute value
                return value < 0 ? `-€${formattedValue}` : `€${formattedValue}`;
            }
            else {
                return value
            }
        },
        id: 'reference',
        stack: 'stack1',
        stackOffset: 'none',
        color: '#5DADE2'
    };

    // Separate counters for positive and negative drivers
    let positiveIndex = 0;
    let negativeIndex = 0;

    // Step 1: Calculate the total of all positive and negative drivers
    const positiveSum = viewValueModel
        .filter(driver => driver.positive_driver)
        .reduce((acc, driver) => acc + driver.value, 0);

    const negativeSum = viewValueModel
        .filter(driver => !driver.positive_driver)
        .reduce((acc, driver) => acc - driver.value, 0);

    // Transform value drivers to data series format for display in the Bar Chart
    const transformValueDriver = (driver, index) => {
        let data = [null, null, null, null];

        if (driver.positive_driver) {
            data[1] = driver.value; // Positive Value Drivers
            positiveIndex = positiveIndex + 1
        } else {
            data[2] = -driver.value; // Negative Value Drivers
            negativeIndex = negativeIndex + 1
        }

        return {
            data: data,
            label: driver.driver_name,
            valueFormatter: (value) => {
                if (value) {
                    const formattedValue = convertAddThsndDivider(Math.abs(value)); // Format the absolute value
                    return value < 0 ? `-€${formattedValue}` : `€${formattedValue}`;
                }
                else {
                    return value
                }
            },
            id: index,
            stack: 'stack1',
            stackOffset: 'none',
            color: driver.positive_driver ? greenShades[positiveIndex % greenShades.length] : redShades[negativeIndex % redShades.length] // Apply green or red shade
        };
    };

    // Calculate and display the total value created
    const totalValueSerie = {
        data: [null, null, null, referenceValue + positiveSum + negativeSum],
        label: 'Your Created Value',
        valueFormatter: (value) => {
            if (value) {
                const formattedValue = convertAddThsndDivider(Math.abs(value)); // Format the absolute value
                return value < 0 ? `-€${formattedValue}` : `€${formattedValue}`;
            }
            else {
                return value
            }
        },
        id: 'totalValue',
        stack: 'stack1',
        stackOffset: 'none',
        color: '#696969'
    };

    // Iterate through drivers and create data series for the chart
    const driverSeries = viewValueModel.map((driver, index) => {
        return transformValueDriver(driver, index);
    });

    const valueSeries = [referenceValueSerie, ...driverSeries, totalValueSerie]

    const xLabels = [
        'Price Competitive Alternative',
        'Positive Value Drivers',
        'Negative Value Drivers',
        'Total Economic Value'
    ];

    // Step 2: Add an invisible bar to make Negative Value Drivers start at the positive sum
    const negativeBaseSeries = {
        data: [null, referenceValue, referenceValue + positiveSum, null], // Null for the positive section, sum for negative section
        label: '', // Empty label so it doesn't show up
        valueFormatter: (value) => {
            if (value) {
                return null
            }
            else if (value === 0) {
                return null
            }
            else {
                return value
            }
        },
        stack: 'stack1', // Add this to the stack so it appears in the same bar
        id: 'negativeBase',
        color: 'transparent' // Make it fully transparent
    };

    const variablesUpdateFormActions = () => {
        return (
            <React.Fragment>
                <Button variant="contained" startIcon={<CalculateIcon />} size="small" type="submit" sx={{ mt: 2 }}>Recalculate</Button>
            </React.Fragment>
        )
    }

    const variablesOnSubmit = (values) => {
        values = removeCommasFromValues(values)
        dispatch(fetchViewValueModel(id, values))

    }

    const renderVariables = () => {
        return (
            <Paper sx={{ maxWidth: 400, padding: 2 }}>
                <Typography variant='subtitle1'>
                    Variables
                </Typography>
                <VariablesForm
                    variables={variables}
                    formActions={variablesUpdateFormActions}
                    onSubmit={variablesOnSubmit}
                />
            </Paper>
        )
    }

    // Filter out Price of Alternative if status quo is true
    const dataSeries = [negativeBaseSeries, ...valueSeries] // Include the base series for negative drivers
    const filteredDataSeries = dataSeries
        // 1) if statusQuo, filter out the unwanted series
        .filter(s => !(statusQuo && s.label === "Price of Alternative"))
        // 2) for each remaining series, drop the first data point
        .map(s => ({
            ...s,
            data: statusQuo && Array.isArray(s.data) ? s.data.slice(1) : s.data
        }));

    const filteredXLabels = statusQuo
        ? xLabels.filter(label => label !== "Price Competitive Alternative")
        : xLabels;


    const renderValueModel = () => {
        return (
            <Box display="flex" id="chart">
                <Box ref={chartRef} sx={{ minWidth: 800 }}>
                    <BarChart
                        height={500}
                        series={filteredDataSeries} //{[negativeBaseSeries, ...valueSeries]} // Include the base series for negative drivers
                        xAxis={[{ data: filteredXLabels, scaleType: 'band' }]} //{[{ data: xLabels, scaleType: 'band' }]}
                        yAxis={[{ valueFormatter: (value) => value >= 0 ? `${'€'}${convertAddThsndDivider(value || '0')}` : `-${'€'}${convertAddThsndDivider(Math.abs(value) || '0')}` }]}
                        margin={{ left: 80, top: 80 }}
                        // padding={{ top: 40, right: 50, bottom: 50, left: 50 }}
                        slotProps={{
                            legend: {
                                itemMarkWidth: 20,
                                itemMarkHeight: 12,
                                markGap: 5,
                                itemGap: 10,
                                labelStyle: {
                                    fontSize: 11,
                                },
                                position: {
                                    vertical: 'top', // or 'bottom'
                                    horizontal: 'right', // or 'left'
                                },
                                direction: 'row', // Align items in a vertical column (if needed)
                                align: 'start', // Align the second line with the first line
                            }
                        }}
                    />
                </Box>

                {/* Vertical line with arrows on the right */}
                {arrowHeight > 0 && referenceValue + positiveSum + negativeSum > 0 ?
                    <Box display="flex" alignItems="center" sx={{ ml: 2 }}>
                        {/* Text description above the line */}
                        <Typography variant="caption" align="center" sx={{ mb: 1, ml: 1 }}>
                            Possible Price Range to capture part of value
                        </Typography>

                        {/* Vertical line with arrows */}
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "space-between",
                                height: `${arrowHeight}px`, // Set to match negative value driver bar height
                                position: "absolute",
                                bottom: distanceFromBottom
                            }}
                        >
                            {/* Arrow up */}
                            <Box
                                sx={{
                                    width: 0,
                                    height: 0,
                                    borderLeft: "5px solid transparent",
                                    borderRight: "5px solid transparent",
                                    borderBottom: "10px solid black",
                                }}
                            />

                            {/* Vertical line */}
                            <Box
                                sx={{
                                    width: "2px",
                                    backgroundColor: "black",
                                    flexGrow: 1,
                                }}
                            />

                            {/* Arrow down */}
                            <Box
                                sx={{
                                    width: 0,
                                    height: 0,
                                    borderLeft: "5px solid transparent",
                                    borderRight: "5px solid transparent",
                                    borderTop: "10px solid black",
                                }}
                            />
                        </Box>
                    </Box>
                    : null}

            </Box>
        )
    }

    return (
        <Box>
            <Grid container>
                <Grid item xs={3}>
                    {renderVariables()}
                </Grid>
                <Grid item xs={8} sx={{ ml: 4 }}>
                    {renderValueModel()}
                </Grid>
            </Grid>
            {/* <ExportChartToPptButton /> */}
        </Box>
    );
};

export default ViewValueModel;